import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../contexts/ProjectContext';
import { ProjectAlert, AlertType } from '../types';
import { BellIcon } from '../components/icons/Icons';
import {
  AlertTriangle, Clock, AlarmClock, CalendarClock,
  UserCheck, MessageSquare, CheckCircle2, X,
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { AppEmptyState, AppSectionHero, AppStatCard } from '../components/common/AppSurface';
import './NotificationsPage.css';

type Group = 'critical' | 'warning' | 'info';
type FilterTab = 'all' | Group;

const ALERT_GROUP: Record<AlertType, Group> = {
  ISSUE_REPORTED:  'critical',
  TASK_OVERDUE:    'critical',
  LATE_COMPLETION: 'warning',
  TASK_DUE_SOON:   'warning',
  TASK_ASSIGNED:   'info',
  NEW_COMMENT:     'info',
};

const ALERT_COLOR: Record<AlertType, string> = {
  ISSUE_REPORTED:  '#ef4444',
  TASK_OVERDUE:    '#ef4444',
  LATE_COMPLETION: '#f59e0b',
  TASK_DUE_SOON:   '#f59e0b',
  TASK_ASSIGNED:   '#3b82f6',
  NEW_COMMENT:     '#3b82f6',
};

function AlertTypeIcon({ type, size = 16 }: { type: AlertType; size?: number }) {
  const color = ALERT_COLOR[type];
  switch (type) {
    case 'ISSUE_REPORTED':  return <AlertTriangle  size={size} color={color} />;
    case 'TASK_OVERDUE':    return <Clock          size={size} color={color} />;
    case 'LATE_COMPLETION': return <AlarmClock     size={size} color={color} />;
    case 'TASK_DUE_SOON':   return <CalendarClock  size={size} color={color} />;
    case 'TASK_ASSIGNED':   return <UserCheck      size={size} color={color} />;
    case 'NEW_COMMENT':     return <MessageSquare  size={size} color={color} />;
  }
}

const ALERT_LABEL: Record<AlertType, string> = {
  ISSUE_REPORTED:  'Issue Reported',
  TASK_OVERDUE:    'Task Overdue',
  LATE_COMPLETION: 'Late Completion',
  TASK_DUE_SOON:   'Due Soon',
  TASK_ASSIGNED:   'Task Assigned',
  NEW_COMMENT:     'New Comment',
};

const GROUP_COLOR: Record<Group, string> = {
  critical: '#ef4444',
  warning:  '#f59e0b',
  info:     '#3b82f6',
};

const TABS: { key: FilterTab; label: string; color?: string }[] = [
  { key: 'all',      label: 'All' },
  { key: 'critical', label: 'Critical', color: '#ef4444' },
  { key: 'warning',  label: 'Warnings', color: '#f59e0b' },
  { key: 'info',     label: 'Info',     color: '#3b82f6' },
];

const PAGE_SIZE = 20;

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, isProjectManager } = useAuth();
  const { alerts, markAlertRead, markAllAlertsRead } = useProjects();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [activeTab, alerts.length]);

  const visible = useMemo(() => {
    if (isProjectManager) return alerts;
    if (!currentUser) return [];
    return alerts.filter(a => a.assignedTo.includes(currentUser.id));
  }, [alerts, isProjectManager, currentUser]);

  const filtered = useMemo(() => {
    if (activeTab === 'all') return visible;
    return visible.filter(a => ALERT_GROUP[a.type] === activeTab);
  }, [visible, activeTab]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const unreadCount = useMemo(() => visible.filter(a => !a.isRead).length, [visible]);

  const counts: Record<FilterTab, number> = useMemo(() => ({
    all:      visible.length,
    critical: visible.filter(a => ALERT_GROUP[a.type] === 'critical').length,
    warning:  visible.filter(a => ALERT_GROUP[a.type] === 'warning').length,
    info:     visible.filter(a => ALERT_GROUP[a.type] === 'info').length,
  }), [visible]);

  const handleClick = (alert: ProjectAlert) => {
    markAlertRead(alert.id);
    navigate(isProjectManager ? `/workspace/${alert.subElementId}` : `/my-tasks/${alert.subElementId}`);
  };

  const handleMarkAllRead = () => {
    markAllAlertsRead();
    showToast('Notifications marked as read', 'info');
  };

  return (
    <div className="notif-page">
      <AppSectionHero
        icon={<BellIcon size={28} />}
        title="Notifications"
        subtitle="Track critical alerts, warnings, comments, and assignments."
        aside={
          <>
            <AppStatCard icon={<BellIcon size={20} />} label="Unread" value={unreadCount} caption="Needs attention" tone="blue" />
            <AppStatCard icon={<CheckCircle2 size={20} />} label="Total" value={visible.length} caption="Visible notifications" tone="mint" />
            {unreadCount > 0 && (
              <button className="app-surface-btn-primary notif-page-mark-all" onClick={handleMarkAllRead} type="button">
                Mark all as read
              </button>
            )}
          </>
        }
      />

      {/* Filter tabs */}
      <div className="notif-page-tabs app-surface-chip-group">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`app-surface-chip-btn notif-page-tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
            type="button"
            style={activeTab === tab.key && tab.color
              ? { borderColor: tab.color, color: tab.color }
              : undefined}
          >
            {tab.color && (
              <span className="notif-tab-dot" style={{ background: tab.color }} />
            )}
            {tab.label}
            {counts[tab.key] > 0 && (
              <span className="app-surface-chip-count notif-page-tab-count">{counts[tab.key]}</span>
            )}
          </button>
        ))}
      </div>

      {/* Notification list */}
      <div className="notif-page-list">
        {filtered.length === 0 ? (
          <AppEmptyState
            className="notif-page-empty"
            icon={<CheckCircle2 size={28} />}
            title={visible.length === 0 ? 'No notifications' : `No ${activeTab} notifications`}
            message={visible.length === 0
              ? 'Assignments, comments, due dates, and issue alerts will appear here.'
              : 'Switch tabs to review other notification groups.'}
          />
        ) : (
          paginated.map(alert => {
            const group = ALERT_GROUP[alert.type];
            return (
              <div
                key={alert.id}
                className={`notif-page-item${!alert.isRead ? ' unread' : ''} group-${group}`}
                onClick={() => handleClick(alert)}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && handleClick(alert)}
              >
                <div className="notif-page-item-bar" style={{ background: GROUP_COLOR[group] }} />
                <span className="notif-page-item-icon">
                  <AlertTypeIcon type={alert.type} />
                </span>
                <div className="notif-page-item-content">
                  <div className="notif-page-item-top">
                    <span className="notif-page-item-type">{ALERT_LABEL[alert.type]}</span>
                    <span className="notif-page-item-code">{alert.taskCode}</span>
                    {!alert.isRead && <span className="notif-page-unread-dot" />}
                  </div>
                  <p className="notif-page-item-msg">{alert.message}</p>
                  <div className="notif-page-item-meta">
                    <span className="notif-page-item-time" title={formatDate(alert.createdAt)}>
                      {timeAgo(alert.createdAt)}
                    </span>
                    {alert.endDate && (
                      <span className="notif-page-item-due">Due: {alert.endDate}</span>
                    )}
                  </div>
                </div>
                {!alert.isRead && (
                  <button
                    className="notif-page-item-dismiss"
                    type="button"
                    onClick={e => { e.stopPropagation(); markAlertRead(alert.id); }}
                    title="Mark as read"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="notif-page-pagination">
          <span className="notif-page-pagination-info">
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div className="notif-page-pagination-controls">
            <button className="notif-page-pag-btn" onClick={() => setPage(1)} disabled={page === 1} title="First page">«</button>
            <button className="notif-page-pag-btn" onClick={() => setPage(p => p - 1)} disabled={page === 1} title="Previous page">‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis');
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === 'ellipsis' ? (
                  <span key={`e-${i}`} className="notif-page-pag-ellipsis">…</span>
                ) : (
                  <button key={p} className={`notif-page-pag-btn${page === p ? ' active' : ''}`} onClick={() => setPage(p as number)}>{p}</button>
                )
              )}
            <button className="notif-page-pag-btn" onClick={() => setPage(p => p + 1)} disabled={page === totalPages} title="Next page">›</button>
            <button className="notif-page-pag-btn" onClick={() => setPage(totalPages)} disabled={page === totalPages} title="Last page">»</button>
          </div>
        </div>
      )}
    </div>
  );
};
